import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  GET_POST_BY_SLUG,
  GET_POSTS_WITHOUT_CAT,
  GET_POSTS_BY_CATEGORY_NAME,
} from "./queries";
import { useQuery, useLazyQuery } from "@apollo/client";
import PostCard from "./PostCard";

export default function PostDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [title, setTitle] = useState("Loading... ");

  const { data, loading, error } = useQuery(GET_POST_BY_SLUG, {
    variables: { slug },
  });

  const post = data?.post;

  useEffect(() => {
    if (post?.title) {
      setTitle(post.title);
    }
  }, [post]);

  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [categorySlug, setCategorySlug] = useState<string | null>(null);

  useEffect(() => {
    if (post?.categories?.nodes) {
      const found = post.categories.nodes.find((c: any) =>
        ["Sports", "Variety", "News", "Opinion"].includes(c.name),
      );
      if (found) {
        setCategoryName(found.name);
        setCategorySlug(found.name.toLowerCase());
      }
    }
  }, [post]);

  const [getPostsByCategory, { data: catData, loading: catLoading }] =
    useLazyQuery(GET_POSTS_BY_CATEGORY_NAME);

  const [getPostsWithoutCategory, { data: postsData, loading: postsLoading }] =
    useLazyQuery(GET_POSTS_WITHOUT_CAT);

  const catPosts = catData?.posts?.nodes || [];
  const posts = postsData?.posts?.nodes || [];

  //  Sets title
  useEffect(() => {
    document.title = title;
  }, [title]);

  const [categoryId, setCategoryId] = useState<string | null>(null);

  // Automatically run when categoryName changes
  useEffect(() => {
    if (categoryName) {
      switch (categoryName) {
        case "News":
          setCategoryId("dGVybToxMDI=");
          break;
        case "Variety":
          setCategoryId("dGVybTo0MzI2");
          break;
        case "Sports":
          setCategoryId("dGVybTo0Mg==");
          break;
        case "Opinion":
          setCategoryId("dGVybTo0MQ==");
          break;
      }
      getPostsByCategory({ variables: { category: categoryName } });
    }
  }, [categoryName, getPostsByCategory]);

  useEffect(() => {
    if (categoryId) {
      getPostsWithoutCategory({ variables: { category: categoryId } });
    }
  }, [categoryId, getPostsWithoutCategory]);

  if (error)
    return (
      <div className="text-center py-20">
        <p className="text-red-600 font-bold text-xl">Error: {error.message}</p>
      </div>
    );

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-12 md:py-20">
        <article>
          {loading ? (
            <div className="mb-12">
              <Skeleton height="3.5rem" className="mb-6" />
              <Skeleton height="1.5rem" width="30%" className="mx-auto" />
            </div>
          ) : (
            <header className="text-center mb-12">
              <h1
                dangerouslySetInnerHTML={{ __html: post.title }}
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-gray-900 leading-tight mb-6"
              />
              <div className="flex items-center justify-center gap-4">
                <span className="h-px w-8 bg-[#0a5640]"></span>
                <Link
                  to={`/category/${categorySlug}`}
                  className="text-lg text-[#0a5640] font-bold uppercase tracking-widest hover:text-[#084533] transition-colors"
                >
                  {categoryName}
                </Link>
                <span className="h-px w-8 bg-[#0a5640]"></span>
              </div>
            </header>
          )}

          <div className="mb-12">
            {loading ? (
              <div className="aspect-video">
                <Skeleton height="100%" className="rounded-xl" />
              </div>
            ) : (
              <figure>
                <img
                  src={
                    post?.featuredImage?.node?.sourceUrl ||
                    post?.featuredImage?.node?.mediaDetails?.sizes?.find(
                      (s: any) => s.name === "full",
                    )?.sourceUrl
                  }
                  alt={post?.featuredImage?.node?.altText || "Featured Image"}
                  className="w-full h-auto rounded-xl shadow-lg"
                />
              </figure>
            )}
          </div>

          <div className="max-w-3xl mx-auto">
            {!loading && (
              <div className="flex flex-col md:flex-row items-center justify-between py-6 border-y border-gray-100 mb-10 gap-6">
                <div className="flex items-center gap-4">
                  <img
                    src={post.author.node.avatar.url}
                    alt={post.author.node.name}
                    className="w-12 h-12 rounded-full ring-2 ring-gray-50 shadow-sm"
                  />
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">
                      Written By
                    </p>
                    <Link
                      to={`/author/${post.author.node.slug}`}
                      className="text-[#0a5640] font-black text-lg hover:underline transition-all"
                    >
                      {post.author.node.name}
                    </Link>
                  </div>
                </div>
                <div className="text-center md:text-right">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">
                    Published On
                  </p>
                  <p className="text-gray-900 font-bold text-lg">
                    {new Date(post.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
            )}

            {loading ? (
              <div className="space-y-6">
                <p>
                  <Skeleton count={15} height="1.2rem" />
                </p>
              </div>
            ) : (
              <div
                // [&>p]:mb-4 tells Tailwind: "Find every <p> inside this div and add margin-bottom"
                className="prose xl:prose-lg [&>p]:mb-4"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            )}

            {!loading && (
              <section className="mt-20 bg-gray-50 rounded-2xl p-8 border border-gray-100">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                  <div className="flex-shrink-0 text-center">
                    <Link to={`/author/${post.author.node.slug}`}>
                      <img
                        src={post.author.node.avatar.url}
                        alt={post.author.node.name}
                        className="w-24 h-24 rounded-full border-4 border-white shadow-md mx-auto mb-4"
                      />
                    </Link>
                    <Link to={`/author/${post.author.node.slug}`}>
                      <h3 className="text-xl font-bold font-heading text-gray-900">
                        {post.author.node.name}
                      </h3>
                    </Link>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xs font-black uppercase text-gray-400 tracking-[0.2em] mb-3 text-center md:text-left">
                      About The Author
                    </h4>
                    <p className="text-gray-600 text-lg leading-relaxed text-center md:text-left">
                      {post.author.node.description ||
                        "The Spectrum is North Dakota State University's student-run newspaper since 1896."}
                    </p>
                  </div>
                </div>
              </section>
            )}
          </div>
        </article>

        {/* Recommendations Section */}
        <section className="mt-24 pt-20 border-t border-gray-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight mb-10 border-b-2 border-gray-900 pb-2 inline-block">
                Latest News
              </h3>
              <div className="space-y-12">
                {postsLoading ? (
                  <div className="space-y-8">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} height={200} className="rounded-xl" />
                    ))}
                  </div>
                ) : (
                  posts.slice(0, 3).map((post: any) => (
                    <div
                      key={post.id}
                      className="transition-transform duration-300 hover:-translate-y-1"
                    >
                      <PostCard post={post} />
                    </div>
                  ))
                )}
              </div>
              <div className="mt-12 text-center">
                <Link
                  to="/articles"
                  className="inline-block border-2 border-[#0a5640] text-[#0a5640] font-black px-8 py-3 rounded-lg hover:bg-[#0a5640] hover:text-white transition-all duration-300 uppercase tracking-widest text-sm"
                >
                  View All Articles
                </Link>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight mb-10 border-b-2 border-gray-900 pb-2 inline-block">
                More In {categoryName}
              </h3>
              <div className="space-y-12">
                {catLoading ? (
                  <div className="space-y-8">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} height={200} className="rounded-xl" />
                    ))}
                  </div>
                ) : (
                  catPosts.slice(0, 3).map((post: any) => (
                    <div
                      key={post.id}
                      className="transition-transform duration-300 hover:-translate-y-1"
                    >
                      <PostCard post={post} />
                    </div>
                  ))
                )}
              </div>
              <div className="mt-12 text-center">
                <Link
                  to={`/category/${categorySlug}`}
                  className="inline-block border-2 border-[#0a5640] text-[#0a5640] font-black px-8 py-3 rounded-lg hover:bg-[#0a5640] hover:text-white transition-all duration-300 uppercase tracking-widest text-sm"
                >
                  More {categoryName}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
